import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Grid } from '@mui/material';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import ReceiptLongIcon    from '@mui/icons-material/ReceiptLong';
import { motion }         from 'framer-motion';
import SectionHeader      from './SectionHeader';
import { C, F, SH }       from '../styles/tokens';

const STATS = [
  { target: 1000, label: 'Companies Incorporated', icon: <BusinessCenterIcon sx={{ fontSize: 32 }} />, color: C.blue },
  { target: 3000, label: 'GST Registrations',      icon: <ReceiptLongIcon    sx={{ fontSize: 32 }} />, color: '#7C3AED' },
];

// ── Format a raw count into display string ────────────────────────────────────

const formatCount = (val) => {
  if (val >= 1000) {
    const k = val / 1000;
    return `${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}k+`;
  }
  if (val >= 100) return `${Math.floor(val / 100) * 100}+`;
  return `${val}+`;
};

// ── Count-up hook (starts when element enters viewport) ───────────────────────

const useCountUp = (target, duration = 1600) => {
  const [count, setCount]   = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStarted(true); },
      { threshold: 0.4 },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    const startTime = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [started, target, duration]);

  return { count, ref };
};

// ── Animated stat card ────────────────────────────────────────────────────────

const AnimatedStat = ({ target, label, icon, color, delay }) => {
  const { count, ref } = useCountUp(target, 1600);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
    >
      <Box
        ref={ref}
        sx={{
          textAlign:       'center',
          backgroundColor: C.bg,
          border:          `1px solid ${C.border}`,
          borderTop:       `4px solid ${color}`,
          borderRadius:    '18px',
          p:               { xs: 3, md: 4 },
          boxShadow:       SH.card,
          transition:      'box-shadow 0.25s, transform 0.25s',
          '&:hover':       { boxShadow: SH.cardHover, transform: 'translateY(-4px)' },
        }}
      >
        <Box sx={{ color, mb: 1.5 }}>{icon}</Box>
        <Typography
          sx={{
            fontFamily: F.heading,
            fontWeight: 800,
            fontSize:   { xs: 48, md: 64 },
            color,
            lineHeight: 1,
            mb:         0.5,
            fontVariantNumeric: 'tabular-nums',
            minWidth:   { xs: 120, md: 160 },
            display:    'inline-block',
          }}
        >
          {formatCount(count)}
        </Typography>
        <Typography sx={{ fontFamily: F.body, fontSize: { xs: 14, md: 16 }, color: C.textMid, fontWeight: 500, mt: 1 }}>
          {label}
        </Typography>
      </Box>
    </motion.div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────

const ClientLogos = () => (
  <Box id="clients" sx={{ backgroundColor: C.white, py: { xs: 7, md: 10 }, px: { xs: 2, md: 6 } }}>
    <SectionHeader
      label="Our Impact"
      title="Numbers That Speak"
      subtitle="A growing track record built on trust, accuracy, and speed."
    />

    <Grid container spacing={4} justifyContent="center" sx={{ maxWidth: 680, mx: 'auto' }}>
      {STATS.map((s, idx) => (
        <Grid item xs={12} sm={6} key={s.label}>
          <AnimatedStat {...s} delay={idx * 0.15} />
        </Grid>
      ))}
    </Grid>
  </Box>
);

export default ClientLogos;
